/**
 * expressionEvaluator.js
 *
 * A small, whitelisted arithmetic expression evaluator.
 *
 * Design parameter formulas live in template JSON as strings (see
 * `designParameters.js`). Those strings come from template files, which are
 * user-supplied data — so they are parsed and walked here rather than handed to
 * `eval` or `new Function`. The grammar has no property access, no assignment,
 * no statements: an expression can only read the identifiers it is given and
 * call the functions on the whitelist below.
 *
 * Grammar (recursive descent):
 *   expr    := term (('+' | '-') term)*
 *   term    := unary (('*' | '/' | '%') unary)*
 *   unary   := ('-' | '+') unary | power
 *   power   := primary ('^' unary)?          // right-associative
 *   primary := number | ident | ident '(' args ')' | '(' expr ')'
 */

'use strict';

// ---------------------------------------------------------------------------
// Function whitelist
// ---------------------------------------------------------------------------

const FUNCTIONS = {
  abs: Math.abs,
  ceil: Math.ceil,
  exp: Math.exp,
  floor: Math.floor,
  ln: Math.log,
  log10: Math.log10,
  max: Math.max,
  min: Math.min,
  pow: Math.pow,
  sqrt: Math.sqrt,
  // round(x) → nearest integer; round(x, n) → n decimal places.
  round: (x, digits) => {
    if (digits === undefined) return Math.round(x);
    const f = Math.pow(10, digits);
    return Math.round(x * f) / f;
  },
};

export const ALLOWED_FUNCTIONS = Object.keys(FUNCTIONS);

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

const NUMBER_RE = /^\d+(\.\d+)?([eE][+-]?\d+)?|^\.\d+([eE][+-]?\d+)?/;
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_]*/;
const OPERATORS = new Set(['+', '-', '*', '/', '%', '^', '(', ')', ',']);

function tokenize(source) {
  const tokens = [];
  let i = 0;

  while (i < source.length) {
    const ch = source[i];

    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }

    if (OPERATORS.has(ch)) {
      tokens.push({ type: 'op', value: ch, pos: i });
      i++;
      continue;
    }

    const rest = source.slice(i);

    const num = NUMBER_RE.exec(rest);
    if (num) {
      tokens.push({ type: 'number', value: parseFloat(num[0]), pos: i });
      i += num[0].length;
      continue;
    }

    const ident = IDENT_RE.exec(rest);
    if (ident) {
      tokens.push({ type: 'ident', value: ident[0], pos: i });
      i += ident[0].length;
      continue;
    }

    throw new Error(`Unexpected character "${ch}" at position ${i}`);
  }

  tokens.push({ type: 'end', value: null, pos: source.length });
  return tokens;
}

// ---------------------------------------------------------------------------
// Parser → AST
// ---------------------------------------------------------------------------

function parse(source) {
  const tokens = tokenize(source);
  let pos = 0;

  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  const expectOp = (value) => {
    const token = peek();
    if (token.type !== 'op' || token.value !== value) {
      throw new Error(`Expected "${value}" at position ${token.pos}`);
    }
    return next();
  };

  const isOp = (value) => {
    const token = peek();
    return token.type === 'op' && token.value === value;
  };

  function parseExpr() {
    let left = parseTerm();
    while (isOp('+') || isOp('-')) {
      const op = next().value;
      left = { kind: 'binary', op, left, right: parseTerm() };
    }
    return left;
  }

  function parseTerm() {
    let left = parseUnary();
    while (isOp('*') || isOp('/') || isOp('%')) {
      const op = next().value;
      left = { kind: 'binary', op, left, right: parseUnary() };
    }
    return left;
  }

  function parseUnary() {
    if (isOp('-') || isOp('+')) {
      const op = next().value;
      return { kind: 'unary', op, operand: parseUnary() };
    }
    return parsePower();
  }

  function parsePower() {
    const base = parsePrimary();
    if (isOp('^')) {
      next();
      // Right-associative, and binds tighter than unary minus on its left:
      // -2^2 === -(2^2), while 2^-1 is allowed.
      return { kind: 'binary', op: '^', left: base, right: parseUnary() };
    }
    return base;
  }

  function parsePrimary() {
    const token = peek();

    if (token.type === 'number') {
      next();
      return { kind: 'number', value: token.value };
    }

    if (token.type === 'ident') {
      next();
      if (isOp('(')) {
        next();
        const args = [];
        if (!isOp(')')) {
          args.push(parseExpr());
          while (isOp(',')) { next(); args.push(parseExpr()); }
        }
        expectOp(')');
        return { kind: 'call', name: token.value, args };
      }
      return { kind: 'ident', name: token.value };
    }

    if (token.type === 'op' && token.value === '(') {
      next();
      const inner = parseExpr();
      expectOp(')');
      return inner;
    }

    throw new Error(`Unexpected ${token.type === 'end' ? 'end of expression' : `token "${token.value}"`} at position ${token.pos}`);
  }

  const ast = parseExpr();
  if (peek().type !== 'end') {
    throw new Error(`Unexpected token "${peek().value}" at position ${peek().pos}`);
  }
  return ast;
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

function evaluateNode(node, scope) {
  switch (node.kind) {
    case 'number':
      return node.value;

    case 'ident': {
      if (!Object.prototype.hasOwnProperty.call(scope, node.name)) {
        throw new Error(`Unknown identifier "${node.name}"`);
      }
      const value = Number(scope[node.name]);
      if (!Number.isFinite(value)) {
        throw new Error(`Identifier "${node.name}" is not a finite number`);
      }
      return value;
    }

    case 'unary': {
      const operand = evaluateNode(node.operand, scope);
      return node.op === '-' ? -operand : operand;
    }

    case 'binary': {
      const left = evaluateNode(node.left, scope);
      const right = evaluateNode(node.right, scope);
      switch (node.op) {
        case '+': return left + right;
        case '-': return left - right;
        case '*': return left * right;
        case '/': return left / right;
        case '%': return left % right;
        case '^': return Math.pow(left, right);
        default: throw new Error(`Unsupported operator "${node.op}"`);
      }
    }

    case 'call': {
      if (!Object.prototype.hasOwnProperty.call(FUNCTIONS, node.name)) {
        throw new Error(`Unknown function "${node.name}()" — allowed: ${ALLOWED_FUNCTIONS.join(', ')}`);
      }
      const args = node.args.map(arg => evaluateNode(arg, scope));
      return FUNCTIONS[node.name](...args);
    }

    default:
      throw new Error(`Unsupported expression node "${node.kind}"`);
  }
}

/**
 * Parse an expression once and return a reusable `(scope) => number` function.
 * Throws on a malformed expression.
 */
export function compileExpression(source) {
  if (typeof source !== 'string' || source.trim() === '') {
    throw new Error('Expression must be a non-empty string');
  }
  const ast = parse(source);
  return (scope = {}) => {
    const result = evaluateNode(ast, scope);
    if (!Number.isFinite(result)) {
      throw new Error(`Expression "${source}" did not produce a finite number`);
    }
    return result;
  };
}

/**
 * Convenience one-shot evaluation. Prefer `compileExpression` in loops.
 */
export function evaluateExpression(source, scope = {}) {
  return compileExpression(source)(scope);
}

/**
 * Collect the identifiers an expression reads, ignoring function names.
 * Used to report which parameter a formula depends on.
 */
export function expressionIdentifiers(source) {
  const found = new Set();
  const walk = (node) => {
    if (node.kind === 'ident') found.add(node.name);
    else if (node.kind === 'unary') walk(node.operand);
    else if (node.kind === 'binary') { walk(node.left); walk(node.right); }
    else if (node.kind === 'call') node.args.forEach(walk);
  };
  walk(parse(source));
  return [...found];
}
